import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import {
  Calendar,
  Download,
  Package,
  DollarSign,
  RefreshCw,
  Truck,
  XCircle,
  ShoppingBag,
  Users,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtCurrency = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const fmtDate = (date) =>
  date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

const toYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const orderAmount = (row) => {
  const final = Number(row?.final_amount);
  if (Number.isFinite(final) && final > 0) return final;
  const cod = Number(row?.cod_amount);
  return Number.isFinite(cod) && cod > 0 ? cod : 0;
};

const isDelivered = (row) => {
  const status = String(row?.current_status_type || row?.booking_status || '').toLowerCase();
  return status === 'delivered';
};

const isCancelled = (row) => {
  const status = String(row?.booking_status || row?.current_status_type || '').toLowerCase();
  return status === 'cancelled' || status === 'canceled';
};

const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1);

async function fetchOrdersInRange(startYmd, endYmd) {
  const pageSize = 1000;
  let from = 0;
  const all = [];

  for (;;) {
    const { data, error } = await serviceRoleClient
      .from('master_orders')
      .select(
        [
          'order_id',
          'customer_name',
          'contact_number',
          'final_amount',
          'cod_amount',
          'payment_mode',
          'booking_status',
          'current_status_type',
          'order_created_at',
          'sr_status_raw_time',
          'telecaller_id',
          'team_lead_id',
          'courier_name',
        ].join(','),
      )
      .gte('order_created_at', `${startYmd}T00:00:00`)
      .lte('order_created_at', `${endYmd}T23:59:59.999`)
      .order('order_created_at', { ascending: false })
      .range(from, from + pageSize - 1);

    if (error) throw error;
    const batch = data || [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return all;
}

async function fetchUsersByIds(ids) {
  const map = new Map();
  const unique = Array.from(new Set((ids || []).filter(Boolean)));
  for (let i = 0; i < unique.length; i += 100) {
    const chunk = unique.slice(i, i + 100);
    const { data, error } = await serviceRoleClient
      .from('users')
      .select('id, name, phone')
      .in('id', chunk);
    if (error) continue;
    (data || []).forEach((u) => map.set(u.id, u));
  }
  return map;
}

const SalesReport = () => {
  const [dateRange, setDateRange] = useState({
    startDate: monthStart(),
    endDate: new Date(),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const startYmd = toYmd(dateRange.startDate);
  const endYmd = toYmd(dateRange.endDate);

  const { data: salesData, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['salesReportBookings', startYmd, endYmd],
    queryFn: async () => {
      const orders = await fetchOrdersInRange(startYmd, endYmd);

      let bookedCount = 0;
      let bookedRevenue = 0;
      let deliveredCount = 0;
      let deliveredRevenue = 0;
      let cancelledCount = 0;
      let cancelledRevenue = 0;
      let prepaidCount = 0;
      let codCount = 0;

      const byDay = new Map(); // ymd -> { booked, delivered, revenue }
      const byTc = new Map(); // id -> stats
      const byTl = new Map();

      const bumpPerson = (map, id, patch) => {
        if (!id) return;
        if (!map.has(id)) {
          map.set(id, { id, booked: 0, delivered: 0, cancelled: 0, bookedRevenue: 0, deliveredRevenue: 0 });
        }
        const row = map.get(id);
        Object.keys(patch).forEach((k) => {
          row[k] += patch[k];
        });
      };

      for (const o of orders) {
        const amt = orderAmount(o);
        const cancelled = isCancelled(o);
        const delivered = !cancelled && isDelivered(o);
        const dayKey = o.order_created_at ? toYmd(new Date(o.order_created_at)) : startYmd;

        if (!byDay.has(dayKey)) byDay.set(dayKey, { date: dayKey, booked: 0, delivered: 0, revenue: 0 });
        const day = byDay.get(dayKey);

        if (cancelled) {
          cancelledCount += 1;
          cancelledRevenue += amt;
          bumpPerson(byTc, o.telecaller_id, { cancelled: 1 });
          bumpPerson(byTl, o.team_lead_id, { cancelled: 1 });
          continue;
        }

        // Active booking
        bookedCount += 1;
        bookedRevenue += amt;
        day.booked += 1;
        day.revenue += amt;

        const pay = String(o.payment_mode || '').toLowerCase();
        if (pay.includes('pre') || pay === 'online') prepaidCount += 1;
        else codCount += 1;

        bumpPerson(byTc, o.telecaller_id, { booked: 1, bookedRevenue: amt });
        bumpPerson(byTl, o.team_lead_id, { booked: 1, bookedRevenue: amt });

        if (delivered) {
          deliveredCount += 1;
          deliveredRevenue += amt;
          day.delivered += 1;
          bumpPerson(byTc, o.telecaller_id, { delivered: 1, deliveredRevenue: amt });
          bumpPerson(byTl, o.team_lead_id, { delivered: 1, deliveredRevenue: amt });
        }
      }

      const userMap = await fetchUsersByIds([
        ...byTc.keys(),
        ...byTl.keys(),
      ]);

      const attachNames = (map) =>
        Array.from(map.values())
          .map((row) => ({
            ...row,
            name: userMap.get(row.id)?.name || 'Unknown',
            phone: userMap.get(row.id)?.phone || '',
          }))
          .sort((a, b) => b.booked - a.booked || b.bookedRevenue - a.bookedRevenue);

      const topTelecallers = attachNames(byTc).slice(0, 15);
      const topTeamLeads = attachNames(byTl).slice(0, 15);

      const dailyTrend = Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));

      const avgOrderValue = bookedCount > 0 ? bookedRevenue / bookedCount : 0;
      const deliveryRate = bookedCount > 0 ? (deliveredCount / bookedCount) * 100 : 0;
      const cancelRate = orders.length > 0 ? (cancelledCount / orders.length) * 100 : 0;

      const funnel = [
        { stage: 'Booked', count: bookedCount, percentage: 100, color: '#4f46e5' },
        {
          stage: 'Delivered',
          count: deliveredCount,
          percentage: deliveryRate,
          color: '#10b981',
        },
        {
          stage: 'Cancelled',
          count: cancelledCount,
          percentage: cancelRate,
          color: '#ef4444',
        },
      ];

      return {
        totalOrders: orders.length,
        bookedCount,
        bookedRevenue,
        deliveredCount,
        deliveredRevenue,
        cancelledCount,
        cancelledRevenue,
        prepaidCount,
        codCount,
        avgOrderValue,
        deliveryRate,
        cancelRate,
        funnel,
        dailyTrend,
        topTelecallers,
        topTeamLeads,
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  const paymentPie = useMemo(() => {
    if (!salesData) return [];
    return [
      { name: 'COD', value: salesData.codCount, color: '#f59e0b' },
      { name: 'Prepaid', value: salesData.prepaidCount, color: '#6366f1' },
    ].filter((x) => x.value > 0);
  }, [salesData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading sales data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Reports</h1>
          <p className="text-gray-600 mt-1">
            Bookings & revenue from master orders · {fmtDate(dateRange.startDate)} – {fmtDate(dateRange.endDate)}
          </p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowDatePicker((v) => !v)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 bg-white"
          >
            <Calendar size={18} />
            <span>Select Date Range</span>
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 bg-white disabled:opacity-60"
          >
            <RefreshCw size={18} className={isFetching ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 bg-white opacity-60 cursor-not-allowed"
            title="Coming soon"
          >
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {showDatePicker ? (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-2">Start Date</span>
              <input
                type="date"
                value={startYmd}
                max={endYmd}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, startDate: new Date(`${e.target.value}T12:00:00`) }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-2">End Date</span>
              <input
                type="date"
                value={endYmd}
                min={startYmd}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: new Date(`${e.target.value}T12:00:00`) }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm ring-1 ring-red-200">
          {error.message || 'Failed to load sales report'}
        </div>
      ) : null}

      {/* Key metrics — booking first */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Booked"
          value={fmtNum(salesData?.bookedCount)}
          sub={`${fmtNum(salesData?.totalOrders)} orders in range`}
          icon={ShoppingBag}
          tone="indigo"
        />
        <MetricCard
          label="Booked Revenue"
          value={fmtCurrency(salesData?.bookedRevenue)}
          sub={`Avg ${fmtCurrency(salesData?.avgOrderValue)} / order`}
          icon={DollarSign}
          tone="emerald"
        />
        <MetricCard
          label="Delivered"
          value={fmtNum(salesData?.deliveredCount)}
          sub={`${(salesData?.deliveryRate || 0).toFixed(1)}% of booked · ${fmtCurrency(salesData?.deliveredRevenue)}`}
          icon={Truck}
          tone="green"
        />
        <MetricCard
          label="Cancelled"
          value={fmtNum(salesData?.cancelledCount)}
          sub={`${(salesData?.cancelRate || 0).toFixed(1)}% · ${fmtCurrency(salesData?.cancelledRevenue)}`}
          icon={XCircle}
          tone="red"
        />
      </div>

      {/* Funnel */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Booking funnel</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(salesData?.funnel || []).map((stage) => (
            <div key={stage.stage}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                <span className="text-sm font-semibold" style={{ color: stage.color }}>
                  {fmtNum(stage.count)} ({Number(stage.percentage || 0).toFixed(1)}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, Math.max(0, stage.percentage || 0))}%`,
                    backgroundColor: stage.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Daily bookings</h2>
          {(salesData?.dailyTrend || []).length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salesData.dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value, name) =>
                    name === 'revenue' ? fmtCurrency(value) : fmtNum(value)
                  }
                />
                <Legend />
                <Bar dataKey="booked" name="Booked" fill="#4f46e5" />
                <Bar dataKey="delivered" name="Delivered" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment mix</h2>
          {paymentPie.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={paymentPie}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${fmtNum(value)}`}
                  outerRadius={90}
                  dataKey="value"
                >
                  {paymentPie.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtNum(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PerformerTable
          title="Top Telecallers"
          icon={Users}
          rows={salesData?.topTelecallers || []}
        />
        <PerformerTable
          title="Top Team Leads"
          icon={Package}
          rows={salesData?.topTeamLeads || []}
        />
      </div>
    </div>
  );
};

function MetricCard({ label, value, sub, icon: Icon, tone }) {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
  };
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.indigo}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">{value}</p>
      {sub ? <p className="text-sm text-gray-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function PerformerTable({ title, icon: Icon, rows }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 overflow-hidden">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Icon size={20} className="text-indigo-600" />
        {title}
      </h2>
      {!rows.length ? (
        <p className="text-sm text-gray-500 py-8 text-center">No booking data in this range.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium text-right">Booked</th>
                <th className="px-3 py-2 font-medium text-right">Booked ₹</th>
                <th className="px-3 py-2 font-medium text-right">Delivered</th>
                <th className="px-3 py-2 font-medium text-right">Delivered ₹</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/80">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{row.name}</div>
                    {row.phone ? <div className="text-xs text-gray-500">{row.phone}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-indigo-700">
                    {fmtNum(row.booked)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-800">
                    {fmtCurrency(row.bookedRevenue)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {fmtNum(row.delivered)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                    {fmtCurrency(row.deliveredRevenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmptyChart() {
  return <div className="h-[300px] flex items-center justify-center text-sm text-gray-500">No data</div>;
}

export default SalesReport;
